import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import classNames from 'classnames'
import Label from '@/elements/Label'

import './OtpInput.scss'

const OTP_LENGTH = 6

interface OtpInputProps {
    value: string
    onChange: (value: string) => void
    label?: string
    instructions?: string
    disabled?: boolean
    autoFocus?: boolean
}

function OtpInput({
    value,
    onChange,
    label,
    instructions,
    disabled = false,
    autoFocus = false,
}: OtpInputProps) {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([])

    const digits = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] ?? '')

    const focusAt = (index: number) => {
        inputRefs.current[Math.max(0, Math.min(OTP_LENGTH - 1, index))]?.focus()
    }

    const updateValue = (nextDigits: string[]) => {
        onChange(nextDigits.join(''))
    }

    const handleChange = (index: number, raw: string) => {
        const char = raw.replace(/\D/g, '').slice(-1)
        if (!char) return

        const next = [...digits]
        next[index] = char
        updateValue(next)

        if (index < OTP_LENGTH - 1) {
            focusAt(index + 1)
        }
    }

    const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace') {
            e.preventDefault()
            const next = [...digits]
            if (next[index]) {
                next[index] = ''
                updateValue(next)
            } else if (index > 0) {
                next[index - 1] = ''
                updateValue(next)
                focusAt(index - 1)
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault()
            focusAt(index - 1)
        } else if (e.key === 'ArrowRight') {
            e.preventDefault()
            focusAt(index + 1)
        }
    }

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
        if (!pasted) return

        const next = [...digits]
        for (let i = 0; i < pasted.length; i++) {
            next[i] = pasted[i]
        }
        updateValue(next)

        focusAt(Math.min(pasted.length, OTP_LENGTH - 1))
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select()
    }

    return (
        <div className='otp-input-root'>
            {label && <Label>{label}</Label>}
            <div className='otp-boxes'>
                {digits.map((digit, i) => (
                    <input
                        key={i}
                        ref={el => { inputRefs.current[i] = el }}
                        className={classNames('otp-box', { filled: !!digit })}
                        type='text'
                        inputMode='numeric'
                        pattern='\d*'
                        maxLength={1}
                        value={digit}
                        autoFocus={autoFocus && i === 0}
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        disabled={disabled}
                        aria-label={`Digit ${i + 1}`}
                        onChange={e => handleChange(i, e.target.value)}
                        onKeyDown={e => handleKeyDown(i, e)}
                        onPaste={handlePaste}
                        onFocus={handleFocus}
                    />
                ))}
            </div>
            {instructions && (
                <span className='otp-label'>{instructions}</span>
            )}
        </div>
    )
}

export default OtpInput
